/* eslint-disable no-case-declarations */
/* eslint-disable react/no-array-index-key */
import React from 'react';
import { Heading, Text, Image, Box, Link } from '@chakra-ui/react';

const paragraphTextProps = {
  lineHeight: 'tall',
  mb: 4,
  whiteSpace: 'pre-wrap',
};

/** Converte \n, \r\n e \r in <br /> per il rendering inline (con formattazione). */
const renderTextWithBreaks = (text) => {
  if (text == null || text === '') return null;
  const parts = String(text).split(/\r?\n|\r/);
  if (parts.length === 1) return parts[0];
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {i > 0 && <br />}
      {part}
    </React.Fragment>
  ));
};

const renderInlineChildren = (children = []) =>
  children.map((child, childIndex) => {
    if (!child) return null;

    if (child.type === 'link' && child.url) {
      return (
        <Link
          key={childIndex}
          href={child.url}
          color="blue.600"
          textDecoration="underline"
          isExternal
        >
          {renderInlineChildren(child.children || [{ text: child.url }])}
        </Link>
      );
    }

    const textContent = renderTextWithBreaks(child.text);
    if (child.bold) {
      return (
        <Text as="span" key={childIndex} fontWeight="bold">
          {textContent}
        </Text>
      );
    }
    if (child.italic) {
      return (
        <Text as="span" key={childIndex} fontStyle="italic">
          {textContent}
        </Text>
      );
    }
    if (child.underline) {
      return (
        <Text as="span" key={childIndex} textDecoration="underline">
          {textContent}
        </Text>
      );
    }

    return <React.Fragment key={childIndex}>{textContent}</React.Fragment>;
  });

const RenderCMSContent = ({ content }) => {
  if (!content) return null;

  if (typeof content === 'string') {
    const trimmed = content.trim();
    if (!trimmed) return null;
    return <Text {...paragraphTextProps}>{trimmed}</Text>;
  }

  if (!Array.isArray(content) || content.length === 0) return null;

  return (
    <>
      {content.map((block, index) => {
        switch (block.type) {
          case 'image':
            return (
              <Box key={index} my={4}>
                <Image
                  src={
                    block.image?.url ||
                    (block.image?.hash && block.image?.ext
                      ? `/uploads/${block.image.hash}${block.image.ext}`
                      : '')
                  }
                  alt={block.image?.alternativeText || 'Immagine CMS'}
                  width="100%"
                  maxWidth={block.image?.width}
                  borderRadius="md"
                  loading="lazy"
                />
                {block.image?.caption && (
                  <Text mt={2} fontSize="sm" color="gray.500">
                    {block.image.caption}
                  </Text>
                )}
              </Box>
            );
          case 'heading': {
            let size;
            switch (block.level) {
              case 1:
                size = '2xl';
                break;
              case 2:
                size = 'xl';
                break;
              case 3:
                size = 'lg';
                break;
              default:
                size = 'md';
            }
            return (
              <Heading
                key={index}
                as={`h${block.level}`}
                size={size}
                mt={4}
                mb={3}
              >
                {renderInlineChildren(block.children || [])}
              </Heading>
            );
          }
          case 'paragraph':
            return (
              <Text key={index} {...paragraphTextProps}>
                {renderInlineChildren(block.children || [])}
              </Text>
            );
          case 'list':
            return (
              <Box
                key={index}
                as={block.format === 'ordered' ? 'ol' : 'ul'}
                pl={6}
                mb={4}
                lineHeight="tall"
              >
                {(block.children || []).map((item, itemIndex) => (
                  <Box as="li" key={itemIndex} mb={2}>
                    {renderInlineChildren(item.children || [])}
                  </Box>
                ))}
              </Box>
            );
          case 'quote':
            return (
              <Box
                key={index}
                borderLeft="4px solid"
                borderColor="gray.300"
                pl={4}
                py={2}
                mb={4}
              >
                <Text lineHeight="tall" fontStyle="italic">
                  {renderInlineChildren(block.children || [])}
                </Text>
              </Box>
            );
          default:
            return null;
        }
      })}
    </>
  );
};

export default RenderCMSContent;
